// Store the promise for an ongoing refresh request
// We store a promise that resolves to the parsed result, not the raw Response,
// to avoid issues with multiple callers trying to consume the same response body
let refreshPromise: Promise<{ success: boolean; csrfToken?: string }> | null = null;

// Internal references to AuthContext functions and state
let _logout: () => Promise<void> = async () => { console.error('ApiClient not initialized with logout'); };
let _setCsrfToken: React.Dispatch<React.SetStateAction<string | null>> = () => { console.error('ApiClient not initialized with setCsrfToken'); };
let _getCsrfToken: () => string | null = () => { console.error('ApiClient not initialized with getCsrfToken'); return null; };

// Initialization function to be called from AuthProvider
export const initializeApiClient = (auth: {
  logout: () => Promise<void>;
  setCsrfToken: React.Dispatch<React.SetStateAction<string | null>>;
  getCsrfToken: () => string | null;
}) => {
  _logout = auth.logout;
  _setCsrfToken = auth.setCsrfToken;
  _getCsrfToken = auth.getCsrfToken;
  console.log('ApiClient initialized');
};

/**
 * A wrapper around fetch that handles authentication, CSRF tokens,
 * and automatically refreshes the session token if a 401 Unauthorized
 * response is received.
 *
 * @param input The URL to fetch or a Request object.
 * @param init Optional fetch options.
 * @returns A Promise resolving to the Response object.
 */
export const authenticatedFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  // We need access to auth context, but hooks can only be called inside components.
  // Instead, we use the initialized functions/values.
  // REMOVED: getAuthContext function

  const makeRequest = async (isRetry = false): Promise<Response> => {
    // Use the internal references
    let csrfToken = _getCsrfToken();
    const logout = _logout;
    const setCsrfToken = _setCsrfToken;

    // Fallback: if not present in context, attempt to read it from the
    // cookie and synchronise the context so subsequent calls have it.
    if (!csrfToken) {
      const cookieToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('csrf_token='))
        ?.split('=')[1];
      if (cookieToken) {
        csrfToken = cookieToken;
        _setCsrfToken(cookieToken);
      }
    }

    // Read DS-Token from shared platform cookie (if present)
    // This is the unified token from the DeepSights platform
    // Cookie name 'ds_token' must match what the platform sets
    const dsTokenRaw = document.cookie
      .split('; ')
      .find((row) => row.startsWith('ds_token='))
      ?.split('=')[1];
    const dsToken = dsTokenRaw ? decodeURIComponent(dsTokenRaw) : undefined;

    const options = { ...init };

    // Ensure headers is a mutable object
    const headers = new Headers(options.headers);

    // Add credentials
    options.credentials = 'include';

    // Add DS-Token header if available (unified token from platform)
    if (dsToken) {
      headers.set('DS-Token', dsToken);
    }

    // Add CSRF token header for non-GET requests
    const method = options.method?.toUpperCase() || 'GET';
    if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
      if (!csrfToken) {
        console.warn(
          'CSRF token is missing for a non-GET request even after cookie fallback. Request may be rejected.'
        );
      }
      headers.set('X-CSRF-Token', csrfToken || '');

      // Ensure Content-Type is set for POST/PUT etc. if body exists
      // Skip setting Content-Type when body is FormData so that the browser can
      // automatically add the correct multipart boundary.
      if (options.body && !headers.has('Content-Type')) {
          if (!(options.body instanceof FormData)) {
              headers.set('Content-Type', 'application/json');
          }
      }
    }
    // Assign the Headers object back to options
    options.headers = headers;

    // Make the actual fetch call
    const response = await fetch(input, options);

    // If unauthorized (and not already a retry of the refresh endpoint itself)
    if (
      response.status === 401 &&
      !isRetry &&
      input.toString() !== '/api/auth/refresh'
    ) {
      console.log('Received 401, attempting token refresh...');

      // Use the existing refresh promise if available, otherwise start a new refresh
      // We wrap the entire refresh flow in a promise that resolves to the parsed result,
      // so multiple concurrent callers can share the same result without consuming the body twice
      if (!refreshPromise) {
        const currentCsrfTokenForRefresh = _getCsrfToken();
        refreshPromise = (async () => {
          try {
            const refreshResponse = await fetch('/api/auth/refresh', {
              method: 'POST',
              credentials: 'include',
              headers: {
                'X-CSRF-Token': currentCsrfTokenForRefresh || '',
                'Content-Type': 'application/json',
              },
            });

            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              return { success: true, csrfToken: refreshData.csrf_token };
            } else {
              return { success: false };
            }
          } catch (error) {
            console.error('Error during token refresh request:', error);
            return { success: false };
          }
        })();
      }

      try {
        const refreshResult = await refreshPromise;

        // Reset promise after all callers have received the result
        // Use setTimeout to ensure concurrent callers get the same promise
        setTimeout(() => { refreshPromise = null; }, 0);

        if (refreshResult.success) {
          console.log('Token refresh successful.');
          if (refreshResult.csrfToken) {
            setCsrfToken(refreshResult.csrfToken);
          } else {
            console.warn("Refresh response did not contain a CSRF token.");
          }

          // Retry the original request
          console.log('Retrying original request...');
          return makeRequest(true);
        } else {
          console.error('Token refresh failed.');
          await logout();
          return response;
        }
      } catch (error) {
        console.error('Error awaiting token refresh:', error);
        refreshPromise = null;
        await logout();
        throw error;
      }
    }

    // Return the original response if not 401 or if it's a retry
    return response;
  };

  return makeRequest();
};

// Helper function to parse JSON response or throw error
export const parseJsonResponse = async (response: Response) => {
  if (!response.ok) {
    let errorPayload: any = { message: `HTTP error! status: ${response.status}` };
    try {
        // Try to parse error details from the backend
        const errorData = await response.json();
        errorPayload = { ...errorPayload, ...errorData };
    } catch (e) {
        // Ignore if response is not JSON or empty
    }
    // Use the error message from backend if available
    throw new Error(errorPayload.error || errorPayload.message || 'API request failed');
  }
  // Handle cases where response might be ok but empty (e.g., 204 No Content)
  if (response.status === 204) {
    return null;
  }
  return response.json();
}; 
